import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import { Icons } from '../components/shared/Icons'
import { useAuth } from '../context/AuthContext'
import { COUNTRY_CODES } from '../data/countryCodes'
import './DraftPages.css'

// Payment method info
const PAYMENT_METHODS = [
  {
    id: 'stripe',
    name: 'Credit/Debit Card',
    icon: 'creditCard',
    fee: '2.9% + EUR 0.25',
    feeNote: 'per transaction',
    time: 'Instant activation',
    description: 'Pay securely with Visa, Mastercard, or American Express',
    comingSoon: true
  },
  {
    id: 'paysera',
    name: 'Paysera',
    icon: 'building',
    fee: '1.6% + EUR 0.29',
    feeNote: 'per transaction',
    time: 'Instant activation',
    description: 'Popular payment method in Baltic and European countries',
    comingSoon: true
  },
  {
    id: 'bank_transfer',
    name: 'Bank Transfer',
    icon: 'landmark',
    fee: 'EUR 0',
    feeNote: 'no processing fee',
    time: '3-5 business days',
    description: 'Direct bank transfer with no additional fees'
  }
]

// Helper to render dynamic icons
const PaymentIcon = ({ name, size }) => {
  const IconComponent = Icons[name]
  return IconComponent ? <IconComponent size={size} /> : null
}

export default function Register() {
  const navigate = useNavigate()
  const {
    register,
    checkEmailAvailable,
    getPlans,
    calculatePrice,
    createStripeCheckout,
    createPayseraOrder,
    createBankTransfer,
    loading: authLoading,
    error: authError,
    clearError
  } = useAuth()

  // Form state
  const [step, setStep] = useState(1)
  const [plans, setPlans] = useState([])
  const [priceCalculation, setPriceCalculation] = useState(null)
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [emailChecking, setEmailChecking] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState(null)

  const [formData, setFormData] = useState({
    // Step 1: Client Information
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    countryCode: '+1',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,

    // Step 2: Plan Selection
    planId: null,
    mwAmount: 1,

    // Step 3: Payment Method
    paymentMethod: null
  })

  // Fetch plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      const fetchedPlans = await getPlans()
      setPlans(fetchedPlans)
      // Default to first plan
      if (fetchedPlans.length > 0) {
        setFormData(prev => ({ ...prev, planId: fetchedPlans[0].id }))
      }
    }
    fetchPlans()
  }, [getPlans])

  // Recalculate price when plan or MW changes
  useEffect(() => {
    const calculate = async () => {
      if (formData.planId && formData.mwAmount > 0) {
        const calc = await calculatePrice(formData.planId, formData.mwAmount)
        setPriceCalculation(calc)
      }
    }
    calculate()
  }, [formData.planId, formData.mwAmount, calculatePrice])

  // Check email availability with debounce
  useEffect(() => {
    if (!formData.email || !formData.email.includes('@')) {
      setEmailAvailable(null)
      return
    }

    const timer = setTimeout(async () => {
      setEmailChecking(true)
      const available = await checkEmailAvailable(formData.email)
      setEmailAvailable(available)
      setEmailChecking(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [formData.email, checkEmailAvailable])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (error) setError('')
    if (authError) clearError()
  }

  const validateStep1 = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required')
      return false
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required')
      return false
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Valid email is required')
      return false
    }
    if (emailAvailable === false) {
      setError('This email is already registered')
      return false
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return false
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    if (!formData.acceptTerms) {
      setError('Please accept the terms and conditions')
      return false
    }
    return true
  }

  const validateStep2 = () => {
    if (!formData.planId) {
      setError('Please select a plan')
      return false
    }
    const selectedPlan = plans.find(p => p.id === formData.planId)
    if (selectedPlan && formData.mwAmount < selectedPlan.min_mw) {
      setError(`Minimum ${selectedPlan.min_mw} MW required for ${selectedPlan.display_name} plan`)
      return false
    }
    return true
  }

  const validateStep3 = () => {
    if (!formData.paymentMethod) {
      setError('Please select a payment method')
      return false
    }
    return true
  }

  const nextStep = () => {
    setError('')
    if (step === 1 && !validateStep1()) return
    if (step === 2 && !validateStep2()) return
    if (step === 3 && !validateStep3()) return

    if (step < 4) {
      setStep(step + 1)
    }
  }

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1)
      setError('')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateStep3()) return

    setStatus('submitting')
    setError('')

    try {
      // Register user with subscription
      const result = await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        phoneNumber: formData.countryCode + formData.phone,
        planId: formData.planId,
        mwAmount: formData.mwAmount,
        paymentMethod: formData.paymentMethod
      })

      if (!result.success) {
        setError(result.error || 'Registration failed')
        setStatus('error')
        return
      }

      const { user, subscription } = result

      // Handle payment based on method
      if (formData.paymentMethod === 'stripe') {
        const stripeResult = await createStripeCheckout({
          userId: user.id,
          subscriptionId: subscription.id,
          planName: plans.find(p => p.id === formData.planId)?.display_name || 'Subscription',
          amountEur: priceCalculation?.annual_price || subscription.annual_price_eur,
          customerEmail: formData.email
        })

        if (stripeResult.success && stripeResult.checkout_url) {
          // Redirect to Stripe Checkout
          window.location.href = stripeResult.checkout_url
        } else {
          setError('Failed to create payment session. Please try again.')
          setStatus('error')
        }
      } else if (formData.paymentMethod === 'paysera') {
        const payseraResult = await createPayseraOrder({
          userId: user.id,
          subscriptionId: subscription.id,
          planName: plans.find(p => p.id === formData.planId)?.display_name || 'Subscription',
          amountEur: priceCalculation?.annual_price || subscription.annual_price_eur,
          customerEmail: formData.email,
          customerName: `${formData.firstName} ${formData.lastName}`
        })

        if (payseraResult.success && payseraResult.payment_url) {
          // Redirect to Paysera
          window.location.href = payseraResult.payment_url
        } else {
          setError('Failed to create payment order. Please try again.')
          setStatus('error')
        }
      } else if (formData.paymentMethod === 'bank_transfer') {
        const bankResult = await createBankTransfer({
          userId: user.id,
          subscriptionId: subscription.id,
          amountEur: priceCalculation?.annual_price || subscription.annual_price_eur
        })

        if (bankResult.success) {
          // Navigate to success page with bank details
          navigate('/register/success', {
            state: {
              type: 'bank_transfer',
              bankDetails: bankResult.bank_details,
              instructions: bankResult.instructions
            }
          })
        } else {
          setError('Failed to generate payment instructions. Please try again.')
          setStatus('error')
        }
      }
    } catch (err) {
      setError(err.message || 'Registration failed')
      setStatus('error')
    }
  }

  const selectedPlan = plans.find(p => p.id === formData.planId)

  return (
    <div className="draft-page">
      <section className="draft-hero demo-hero register-hero">
        <div className="container">
          <ScrollReveal>
            <div className="register-header">
              <span className="draft-badge">Get Started</span>
              <h1 className="draft-title">
                Create Your{' '}
                <GradientText gradient="solar">Account</GradientText>
              </h1>
              <p className="draft-subtitle">
                Start monitoring your solar assets with AI-powered insights
              </p>
            </div>
          </ScrollReveal>

          {/* Progress Steps */}
          <ScrollReveal delay={0.1}>
            <div className="register-progress">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`progress-step ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}
                >
                  <div className="step-number">
                    {s < step ? <Icons.check size={16} /> : s}
                  </div>
                  <span className="step-label">
                    {s === 1 && 'Account'}
                    {s === 2 && 'Plan'}
                    {s === 3 && 'Payment'}
                    {s === 4 && 'Confirm'}
                  </span>
                </div>
              ))}
              <div className="progress-line">
                <div
                  className="progress-fill"
                  style={{ width: `${((step - 1) / 3) * 100}%` }}
                />
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <motion.div
              className="register-form-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {error && (
                <div className="form-error-banner">
                  <Icons.alertCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="register-form">
                <AnimatePresence mode="wait">
                  {/* Step 1: Client Information */}
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="register-step"
                    >
                      <h2 className="step-title">Account Information</h2>
                      <p className="step-description">Enter your details to create your account</p>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="firstName">First Name *</label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="John"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="lastName">Last Name *</label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Doe"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <div className="input-with-status">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="you@company.com"
                            required
                            className={emailAvailable === false ? 'input-error' : ''}
                          />
                          {emailChecking && <span className="input-status checking">Checking...</span>}
                          {!emailChecking && emailAvailable === true && (
                            <span className="input-status available">
                              <Icons.check size={16} /> Available
                            </span>
                          )}
                          {!emailChecking && emailAvailable === false && (
                            <span className="input-status taken">
                              <Icons.x size={16} /> Already registered
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="companyName">Company Name</label>
                        <input
                          type="text"
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          placeholder="Your Company Ltd"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <div className="phone-input-group">
                          <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleChange}
                            className="country-code-select"
                          >
                            {COUNTRY_CODES.map((cc) => (
                              <option key={cc.code} value={cc.code}>
                                {cc.code} ({cc.country})
                              </option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="555-0123"
                            className="phone-input"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <div className="password-input-wrapper">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="At least 8 characters"
                            required
                            minLength={8}
                          />
                          <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <Icons.eyeOff size={20} /> : <Icons.eye size={20} />}
                          </button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password *</label>
                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Re-enter your password"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="acceptTerms"
                            checked={formData.acceptTerms}
                            onChange={handleChange}
                          />
                          <span>
                            I agree to the{' '}
                            <Link to="/terms" className="form-link">Terms of Service</Link>
                            {' '}and{' '}
                            <Link to="/privacy" className="form-link">Privacy Policy</Link>
                          </span>
                        </label>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Plan Selection */}
                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="register-step"
                    >
                      <h2 className="step-title">Select Your Plan</h2>
                      <p className="step-description">Choose the plan that fits your solar monitoring needs</p>

                      <div className="plan-cards">
                        {plans.map((plan) => (
                          <div
                            key={plan.id}
                            className={`plan-card ${formData.planId === plan.id ? 'selected' : ''} ${plan.features?.popular ? 'popular' : ''}`}
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              planId: plan.id,
                              mwAmount: Math.max(prev.mwAmount, plan.min_mw)
                            }))}
                          >
                            {plan.features?.popular && <span className="plan-badge">Most Popular</span>}
                            <h3 className="plan-name">{plan.display_name}</h3>
                            <p className="plan-tagline">{plan.features?.tagline}</p>
                            <div className="plan-price">
                              <span className="price-amount">EUR {plan.price_per_mw_eur}</span>
                              <span className="price-period">/MW/year</span>
                            </div>
                            <p className="plan-min">Minimum: {plan.min_mw} MW</p>
                            <div className="plan-radio">
                              <div className={`radio-dot ${formData.planId === plan.id ? 'checked' : ''}`} />
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mw-selector">
                        <label htmlFor="mwAmount">Installation Size (MW)</label>
                        <div className="mw-input-group">
                          <button
                            type="button"
                            className="mw-btn"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              mwAmount: Math.max(selectedPlan?.min_mw || 1, prev.mwAmount - 1)
                            }))}
                            disabled={formData.mwAmount <= (selectedPlan?.min_mw || 1)}
                          >
                            <Icons.minus size={20} />
                          </button>
                          <input
                            type="number"
                            id="mwAmount"
                            name="mwAmount"
                            value={formData.mwAmount}
                            onChange={handleChange}
                            min={selectedPlan?.min_mw || 1}
                            step={1}
                            className="mw-input"
                          />
                          <button
                            type="button"
                            className="mw-btn"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              mwAmount: prev.mwAmount + 1
                            }))}
                          >
                            <Icons.plus size={20} />
                          </button>
                        </div>
                      </div>

                      {priceCalculation && (
                        <div className="price-summary">
                          <div className="price-row">
                            <span>Base price ({formData.mwAmount} MW x EUR {priceCalculation.price_per_mw})</span>
                            <span>EUR {priceCalculation.base_price}</span>
                          </div>
                          {priceCalculation.discount && (
                            <div className="price-row discount">
                              <span>{priceCalculation.discount.label}</span>
                              <span>-EUR {priceCalculation.discount.amount}</span>
                            </div>
                          )}
                          <div className="price-row total">
                            <span>Annual Total</span>
                            <span>EUR {priceCalculation.annual_price}</span>
                          </div>
                          <p className="price-monthly">
                            (EUR {priceCalculation.monthly_equivalent}/month equivalent)
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Step 3: Payment Method */}
                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="register-step"
                    >
                      <h2 className="step-title">Payment Method</h2>
                      <p className="step-description">Choose how you'd like to pay for your subscription</p>

                      <div className="payment-methods">
                        {PAYMENT_METHODS.map((method) => (
                          <div
                            key={method.id}
                            className={`payment-method-card ${formData.paymentMethod === method.id ? 'selected' : ''} ${method.comingSoon ? 'coming-soon' : ''}`}
                            onClick={() => !method.comingSoon && setFormData(prev => ({ ...prev, paymentMethod: method.id }))}
                            style={method.comingSoon ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                          >
                            <div className="payment-method-header">
                              <div className="payment-method-icon">
                                <PaymentIcon name={method.icon} size={24} />
                              </div>
                              <div className="payment-method-info">
                                <h4>{method.name}</h4>
                                <p>{method.description}</p>
                              </div>
                              {method.comingSoon ? (
                                <div className="coming-soon-badge" style={{
                                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                                  color: '#0a0a1a',
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  Coming Soon
                                </div>
                              ) : (
                                <div className="payment-method-radio">
                                  <div className={`radio-dot ${formData.paymentMethod === method.id ? 'checked' : ''}`} />
                                </div>
                              )}
                            </div>
                            <div className="payment-method-details">
                              <div className="detail">
                                <span className="detail-label">Processing fee:</span>
                                <span className="detail-value">{method.fee} <small>{method.feeNote}</small></span>
                              </div>
                              <div className="detail">
                                <span className="detail-label">Activation:</span>
                                <span className="detail-value">{method.time}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {priceCalculation && formData.paymentMethod && (
                        <div className="payment-summary">
                          <h4>Payment Summary</h4>
                          <div className="summary-row">
                            <span>{selectedPlan?.display_name} Plan - {formData.mwAmount} MW</span>
                            <span>EUR {priceCalculation.annual_price}</span>
                          </div>
                          {formData.paymentMethod !== 'bank_transfer' && priceCalculation.payment_fees && (
                            <div className="summary-row fee">
                              <span>Processing fee ({PAYMENT_METHODS.find(m => m.id === formData.paymentMethod)?.fee})</span>
                              <span>EUR {priceCalculation.payment_fees[formData.paymentMethod]?.total || '0.00'}</span>
                            </div>
                          )}
                          <div className="summary-row total">
                            <span>Total to pay</span>
                            <span>
                              EUR {(
                                priceCalculation.annual_price +
                                (priceCalculation.payment_fees?.[formData.paymentMethod]?.total || 0)
                              ).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Step 4: Confirmation */}
                  {step === 4 && (
                    <motion.div
                      key="step4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="register-step"
                    >
                      <h2 className="step-title">Review & Confirm</h2>
                      <p className="step-description">Please review your order before completing the registration</p>

                      <div className="confirmation-summary">
                        <div className="summary-section">
                          <h4>Account Details</h4>
                          <div className="summary-grid">
                            <div>
                              <span className="label">Name</span>
                              <span className="value">{formData.firstName} {formData.lastName}</span>
                            </div>
                            <div>
                              <span className="label">Email</span>
                              <span className="value">{formData.email}</span>
                            </div>
                            {formData.companyName && (
                              <div>
                                <span className="label">Company</span>
                                <span className="value">{formData.companyName}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="summary-section">
                          <h4>Subscription</h4>
                          <div className="summary-grid">
                            <div>
                              <span className="label">Plan</span>
                              <span className="value">{selectedPlan?.display_name}</span>
                            </div>
                            <div>
                              <span className="label">Installation Size</span>
                              <span className="value">{formData.mwAmount} MW</span>
                            </div>
                            <div>
                              <span className="label">Annual Price</span>
                              <span className="value">EUR {priceCalculation?.annual_price}</span>
                            </div>
                          </div>
                        </div>

                        <div className="summary-section">
                          <h4>Payment</h4>
                          <div className="summary-grid">
                            <div>
                              <span className="label">Method</span>
                              <span className="value">
                                {PAYMENT_METHODS.find(m => m.id === formData.paymentMethod)?.name}
                              </span>
                            </div>
                            <div>
                              <span className="label">Processing Time</span>
                              <span className="value">
                                {PAYMENT_METHODS.find(m => m.id === formData.paymentMethod)?.time}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="summary-total">
                          <span>Total Amount</span>
                          <span className="total-amount">
                            EUR {(
                              (priceCalculation?.annual_price || 0) +
                              (priceCalculation?.payment_fees?.[formData.paymentMethod]?.total || 0)
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="register-nav">
                  {step > 1 && (
                    <GlowButton
                      variant="outline"
                      onClick={prevStep}
                      type="button"
                    >
                      <Icons.arrowLeft size={20} />
                      Back
                    </GlowButton>
                  )}

                  {step < 4 ? (
                    <GlowButton
                      variant="solar"
                      onClick={nextStep}
                      type="button"
                      className="nav-next"
                    >
                      Continue
                      <Icons.arrowRight size={20} />
                    </GlowButton>
                  ) : (
                    <GlowButton
                      variant="solar"
                      type="submit"
                      className="nav-next"
                      disabled={status === 'submitting' || authLoading}
                    >
                      {status === 'submitting' ? (
                        <>
                          <span className="loading-spinner" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Complete Registration
                          <Icons.check size={20} />
                        </>
                      )}
                    </GlowButton>
                  )}
                </div>

                <p className="form-footer-text">
                  Already have an account?{' '}
                  <Link to="/signin" className="form-link">Sign in</Link>
                </p>
              </form>
            </motion.div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
